/**
 * scheduleDraftDataService — ScheduleDraft Feature 的 HTTP 数据边界。
 *
 * 本模块封装 API v3 ScheduleDraft 请求与响应解包，向 hooks 暴露
 * 以草稿、轨、航班和来源轨为语义的调用接口。组件和 Context 不直接感知
 * 后端 API 路径、响应 envelope 或 v3 schedule 契约细节。
 *
 * 设计文档 → docs/20_workspace_ux/28_frontend_code_map_and_data_flow.md
 * 骨架关联 → hooks/useScheduleDraftDetail.js (读取入口), hooks/useScheduleDraftFlightMutations.js (航班写入), hooks/useScheduleDraftEditorMutations.js (编辑器写入编排)
 */

import { scheduleDraftApi } from "../../../../api/v3/scheduleDraftApi";
import { unwrapV3ResponseData } from "../../../../api/v3/responseContract";

async function unwrapResponse(requestPromise) {
    const response = await requestPromise;
    return unwrapV3ResponseData(response);
}

export const scheduleDraftDataService = {
    getDraftList(params = {}) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftList(params));
    },

    getDraftDetail(draftId) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftById(draftId));
    },

    createDraft(payload) {
        return unwrapResponse(scheduleDraftApi.createScheduleDraft(payload));
    },

    deleteDraft(draftId) {
        return unwrapResponse(scheduleDraftApi.deleteScheduleDraft(draftId));
    },

    updateDraft(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.updateScheduleDraft(draftId, payload));
    },

    getTracks(draftId) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftTracks(draftId));
    },

    createTrack(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.createScheduleDraftTrack(draftId, payload));
    },

    updateTrack(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.updateScheduleDraftTrack(draftId, trackId, payload));
    },

    deleteTrack(draftId, trackId) {
        return unwrapResponse(scheduleDraftApi.deleteScheduleDraftTrack(draftId, trackId));
    },

    saveTrackAsHistory(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.saveScheduleDraftTrackAsHistory(draftId, trackId, payload));
    },

    restoreTrack(draftId, trackId) {
        return unwrapResponse(scheduleDraftApi.restoreScheduleDraftTrack(draftId, trackId));
    },

    adoptAgentTrack(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.adoptScheduleDraftAgentTrack(draftId, trackId, payload));
    },

    finalizeTrack(draftId, trackId) {
        return unwrapResponse(scheduleDraftApi.finalizeScheduleDraftTrack(draftId, trackId));
    },

    listPublications(draftId, params = {}) {
        return unwrapResponse(scheduleDraftApi.listScheduleDraftPublications(draftId, params));
    },

    getPublication(draftId, publicationId) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftPublication(draftId, publicationId));
    },

    addFlightToTrack(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.addScheduleDraftFlightToTrack(draftId, trackId, payload));
    },

    updateFlight(draftId, flightId, payload) {
        return unwrapResponse(scheduleDraftApi.updateScheduleDraftFlight(draftId, flightId, payload));
    },

    deleteFlight(draftId, flightId) {
        return unwrapResponse(scheduleDraftApi.deleteScheduleDraftFlight(draftId, flightId));
    },

    moveFlight(draftId, flightId, payload) {
        return unwrapResponse(scheduleDraftApi.moveScheduleDraftFlight(draftId, flightId, payload));
    },

    autoCalculateFlights(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.autoCalculateScheduleDraftFlights(draftId, payload));
    },

    analyzeDraft(draftId, trackId, payload = {}) {
        return unwrapResponse(scheduleDraftApi.analyzeScheduleDraft(draftId, trackId, payload));
    },

    getFlightNumberSuggestions(draftId, pairs) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftFlightNumberSuggestions(draftId, pairs));
    },

    previewSourceFlights(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.previewScheduleDraftSourceFlights(draftId, payload));
    },

    importSourceFlights(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.importScheduleDraftSourceFlights(draftId, payload));
    },

    previewTableImport(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.previewScheduleDraftTableImport(draftId, payload));
    },

    importTableFlights(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.importScheduleDraftTableFlights(draftId, payload));
    },

    attachSourceReferenceTrack(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.attachSourceReferenceTrack(draftId, payload));
    },

    ensureComparisonBaseline(draftId, trackId, payload = { run_auto_match: false }) {
        return unwrapResponse(scheduleDraftApi.ensureComparisonBaseline(draftId, trackId, payload));
    },

    bindComparisonBaseline(draftId, trackId, referenceTrackId, options = {}) {
        return unwrapResponse(scheduleDraftApi.bindComparisonBaseline(draftId, trackId, {
            reference_track_id: referenceTrackId,
            run_auto_match: false,
            ...options,
        }));
    },

    triggerAgent(draftId, payload = {}) {
        const { strategy_prompt_ids, aircraft_reg, source_track_id, constraint_overrides = [] } = payload;
        return unwrapResponse(scheduleDraftApi.triggerScheduleDraftAgent(draftId, {
            strategy_prompt_ids,
            aircraft_reg,
            source_track_id,
            constraint_overrides,
        }));
    },

    getAgentSessions(draftId) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftAgentSessions(draftId));
    },

    getAgentStrategyPrompts(params = {}) {
        return unwrapResponse(scheduleDraftApi.getScheduleDraftAgentStrategyPrompts(params));
    },

    autoCalculate(draftId, payload) {
        return unwrapResponse(scheduleDraftApi.autoCalculateScheduleDraftFlights(draftId, payload));
    },

    // Match API
    getTrackMatches(draftId, trackId, referenceTrackId) {
        const params = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.getTrackMatches(draftId, trackId, params));
    },

    createMatch(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.createMatch(draftId, trackId, payload));
    },

    autoMatchTrack(draftId, trackId, referenceTrackId) {
        const payload = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.autoMatchTrack(draftId, trackId, payload));
    },

    autoMatchPreview(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.autoMatchPreview(draftId, trackId, payload));
    },

    validateMatchPreview(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.validateMatchPreview(draftId, trackId, payload));
    },

    commitMatches(draftId, trackId, payload) {
        return unwrapResponse(scheduleDraftApi.commitMatches(draftId, trackId, payload));
    },

    getCompareConfirmation(draftId, trackId, referenceTrackId) {
        const params = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.getCompareConfirmation(draftId, trackId, params));
    },

    confirmCompare(draftId, trackId, referenceTrackId) {
        const payload = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.confirmCompare(draftId, trackId, payload));
    },

    updateMatch(draftId, trackId, matchId, payload, referenceTrackId) {
        return unwrapResponse(scheduleDraftApi.updateMatch(draftId, trackId, matchId, {
            ...payload,
            ...(referenceTrackId ? { reference_track_id: referenceTrackId } : {}),
        }));
    },

    deleteMatch(draftId, trackId, matchId, referenceTrackId) {
        const params = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.deleteMatch(draftId, trackId, matchId, params));
    },

    batchResetMatches(draftId, trackId, referenceTrackId) {
        const payload = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.batchResetMatches(draftId, trackId, payload));
    },

    validateMatches(draftId, trackId, referenceTrackId) {
        const params = referenceTrackId ? { reference_track_id: referenceTrackId } : {};
        return unwrapResponse(scheduleDraftApi.validateMatches(draftId, trackId, params));
    },

    getPublishValidation(draftId, trackId) {
        return unwrapResponse(scheduleDraftApi.getPublishValidation(draftId, trackId));
    },
};

export default scheduleDraftDataService;
